import React from "react";
import { Check, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export default function MultiSelect({ label, options, selected, onChange, testid }) {
  const allSelected = selected.length === options.length;
  const summary = allSelected ? "All" : selected.length === 0 ? "None" : `${selected.length} selected`;
  const grouped = options.some((o) => o.group);

  const toggle = (id) =>
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  const Item = ({ o }) => {
    const on = selected.includes(o.id);
    return (
      <button
        key={o.id}
        data-testid={`${testid}-${o.id}`}
        onClick={() => toggle(o.id)}
        className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-white/5 transition-colors"
      >
        <span className={`w-4 h-4 grid place-items-center border shrink-0 ${on ? "bg-ec-volt border-ec-volt" : "border-white/30"}`}>
          {on && <Check size={12} className="text-black" strokeWidth={3} />}
        </span>
        {o.color && <span className="w-2.5 h-2.5 shrink-0" style={{ backgroundColor: o.color }} />}
        <span className="text-sm truncate">{o.label}</span>
      </button>
    );
  };

  const renderGrouped = () => {
    const order = [];
    const map = {};
    options.forEach((o) => {
      const g = o.group || "other";
      if (!map[g]) { map[g] = { label: o.groupLabel || g, items: [] }; order.push(g); }
      map[g].items.push(o);
    });
    return order.map((g) => {
      const ids = map[g].items.map((o) => o.id);
      const allOn = ids.every((id) => selected.includes(id));
      const toggleGroup = () =>
        onChange(allOn ? selected.filter((x) => !ids.includes(x)) : [...new Set([...selected, ...ids])]);
      return (
        <div key={g}>
          <button
            data-testid={`${testid}-group-${g}`}
            onClick={toggleGroup}
            className="w-full flex items-center justify-between px-3 py-1.5 bg-black/40 hover:bg-black/60 transition-colors sticky top-0"
          >
            <span className="overline text-zinc-400">{map[g].label}</span>
            <span className="overline text-ec-volt">{allOn ? "All" : "Pick"}</span>
          </button>
          {map[g].items.map((o) => <Item key={o.id} o={o} />)}
        </div>
      );
    });
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid={`${testid}-trigger`}
          className="inline-flex items-center justify-between gap-3 overline border border-white/15 px-4 py-2.5 min-w-[190px] hover:border-ec-volt/60 transition-colors text-zinc-200"
        >
          <span>{label}: <span className="text-ec-volt">{summary}</span></span>
          <ChevronDown size={14} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="bg-ec-surface border-white/10 text-white p-0 w-64">
        <div className="flex border-b border-white/10">
          <button data-testid={`${testid}-all`} onClick={() => onChange(options.map((o) => o.id))} className="flex-1 overline py-2.5 hover:bg-white/5 transition-colors">Select all</button>
          <button data-testid={`${testid}-clear`} onClick={() => onChange([])} className="flex-1 overline py-2.5 border-l border-white/10 hover:bg-white/5 transition-colors">Clear</button>
        </div>
        <div className="max-h-72 overflow-y-auto py-1">
          {grouped ? renderGrouped() : options.map((o) => <Item key={o.id} o={o} />)}
        </div>
      </PopoverContent>
    </Popover>
  );
}
